import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, Column } from '@/components/ui/data-table';
import {
  rpxAdjustmentsService,
  LTVAdjustment,
  SASBPremium,
  LTVAdjustmentCreate,
  SASBPremiumCreate,
} from '@/services/rpx-adjustments.service';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Save,
  X,
  Edit,
  Trash2,
  TrendingUp,
  DollarSign,
  BarChart3,
  Settings,
} from 'lucide-react';

interface LTVAdjustmentDisplay extends LTVAdjustment {
  id?: number;
}

interface SASBPremiumDisplay extends SASBPremium {
  id?: number;
}

const RPXAdjustmentsPage: React.FC = () => {
  const [ltvAdjustments, setLtvAdjustments] = useState<LTVAdjustmentDisplay[]>(
    []
  );
  const [sasbPremiums, setSasbPremiums] = useState<SASBPremiumDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ltv' | 'sasb'>('ltv');

  // Form states
  const [showLtvForm, setShowLtvForm] = useState(false);
  const [showSasbForm, setShowSasbForm] = useState(false);
  const [editingLtv, setEditingLtv] = useState<LTVAdjustmentDisplay | null>(
    null
  );
  const [editingSasb, setEditingSasb] = useState<SASBPremiumDisplay | null>(
    null
  );

  // Form data
  const [ltvFormData, setLtvFormData] = useState<LTVAdjustmentCreate>({
    ltv_min: 0,
    ltv_max: 0,
    adjustment_factor: 1,
    description: '',
  });

  const [sasbFormData, setSasbFormData] = useState<SASBPremiumCreate>({
    loan_size_threshold: 50000000,
    premium_rate: 0.005,
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ltvData, sasbData] = await Promise.all([
        rpxAdjustmentsService.getLTVAdjustments(),
        rpxAdjustmentsService.getSASBPremiums(),
      ]);

      // Transform data to include IDs for display
      const transformedLtv: LTVAdjustmentDisplay[] = ltvData.map(
        (item, index) => ({
          ...item,
          id: index,
        })
      );

      const transformedSasb: SASBPremiumDisplay[] = sasbData.map(
        (item, index) => ({
          ...item,
          id: index,
        })
      );

      setLtvAdjustments(transformedLtv);
      setSasbPremiums(transformedSasb);
    } catch (error) {
      console.error('Error loading RPX adjustments:', error);
      toast.error('Failed to load RPX adjustments');
    } finally {
      setLoading(false);
    }
  };

  // LTV Adjustments handlers
  const handleAddLtvAdjustment = async () => {
    try {
      await rpxAdjustmentsService.updateLTVAdjustments([ltvFormData]);
      toast.success('LTV adjustment added successfully');
      setShowLtvForm(false);
      setLtvFormData({
        ltv_min: 0,
        ltv_max: 0,
        adjustment_factor: 1,
        description: '',
      });
      loadData();
    } catch (error) {
      console.error('Error adding LTV adjustment:', error);
      toast.error('Failed to add LTV adjustment');
    }
  };

  const handleUpdateLtvAdjustments = async () => {
    try {
      const adjustmentsToUpdate = ltvAdjustments.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...adjustment } = item;
        return adjustment;
      });
      await rpxAdjustmentsService.replaceLTVAdjustments(adjustmentsToUpdate);
      toast.success('LTV adjustments updated successfully');
      setEditingLtv(null);
      loadData();
    } catch (error) {
      console.error('Error updating LTV adjustments:', error);
      toast.error('Failed to update LTV adjustments');
    }
  };

  const handleEditLtv = (adjustment: LTVAdjustmentDisplay) => {
    setEditingLtv(adjustment);
  };

  const handleSaveLtvEdit = () => {
    if (editingLtv) {
      const updatedAdjustments = ltvAdjustments.map((adj) =>
        adj.id === editingLtv.id ? editingLtv : adj
      );
      setLtvAdjustments(updatedAdjustments);
      handleUpdateLtvAdjustments();
    }
  };

  const handleDeleteLtv = (adjustmentToDelete: LTVAdjustmentDisplay) => {
    const updatedAdjustments = ltvAdjustments.filter(
      (adj) => adj.id !== adjustmentToDelete.id
    );
    setLtvAdjustments(updatedAdjustments);
    // Auto-save after deletion
    setTimeout(() => {
      const adjustmentsToUpdate = updatedAdjustments.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...adjustment } = item;
        return adjustment;
      });
      rpxAdjustmentsService
        .replaceLTVAdjustments(adjustmentsToUpdate)
        .then(() => {
          toast.success('LTV adjustment deleted successfully');
          loadData();
        })
        .catch(() => {
          toast.error('Failed to delete LTV adjustment');
          loadData(); // Reload to revert changes
        });
    }, 100);
  };

  // SASB Premiums handlers
  const handleAddSasbPremium = async () => {
    try {
      await rpxAdjustmentsService.updateSASBPremiums([sasbFormData]);
      toast.success('SASB premium added successfully');
      setShowSasbForm(false);
      setSasbFormData({
        loan_size_threshold: 50000000,
        premium_rate: 0.005,
        description: '',
      });
      loadData();
    } catch (error) {
      console.error('Error adding SASB premium:', error);
      toast.error('Failed to add SASB premium');
    }
  };

  const handleUpdateSasbPremiums = async () => {
    try {
      const premiumsToUpdate = sasbPremiums.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...premium } = item;
        return premium;
      });
      await rpxAdjustmentsService.replaceSASBPremiums(premiumsToUpdate);
      toast.success('SASB premiums updated successfully');
      setEditingSasb(null);
      loadData();
    } catch (error) {
      console.error('Error updating SASB premiums:', error);
      toast.error('Failed to update SASB premiums');
    }
  };

  const handleEditSasb = (premium: SASBPremiumDisplay) => {
    setEditingSasb(premium);
  };

  const handleSaveSasbEdit = () => {
    if (editingSasb) {
      const updatedPremiums = sasbPremiums.map((prem) =>
        prem.id === editingSasb.id ? editingSasb : prem
      );
      setSasbPremiums(updatedPremiums);
      handleUpdateSasbPremiums();
    }
  };

  const handleDeleteSasb = (premiumToDelete: SASBPremiumDisplay) => {
    const updatedPremiums = sasbPremiums.filter(
      (prem) => prem.id !== premiumToDelete.id
    );
    setSasbPremiums(updatedPremiums);
    // Auto-save after deletion
    setTimeout(() => {
      const premiumsToUpdate = updatedPremiums.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...premium } = item;
        return premium;
      });
      rpxAdjustmentsService
        .replaceSASBPremiums(premiumsToUpdate)
        .then(() => {
          toast.success('SASB premium deleted successfully');
          loadData();
        })
        .catch(() => {
          toast.error('Failed to delete SASB premium');
          loadData(); // Reload to revert changes
        });
    }, 100);
  };

  // Utility functions
  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(3)}%`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Table columns
  const ltvColumns: Column<LTVAdjustmentDisplay>[] = [
    {
      header: 'LTV Min (%)',
      accessor: (row) => `${(row.ltv_min * 100).toFixed(1)}%`,
    },
    {
      header: 'LTV Max (%)',
      accessor: (row) => `${(row.ltv_max * 100).toFixed(1)}%`,
    },
    {
      header: 'Adjustment Factor',
      accessor: (row) => {
        if (editingLtv?.id === row.id) {
          return (
            <Input
              type="number"
              step="0.001"
              value={editingLtv?.adjustment_factor || 0}
              onChange={(e) =>
                editingLtv &&
                setEditingLtv({
                  ...editingLtv,
                  adjustment_factor: parseFloat(e.target.value) || 0,
                })
              }
              className="w-24"
              data-test-id="edit-adjustment-factor-input"
            />
          );
        }
        return row.adjustment_factor.toFixed(3);
      },
    },
    {
      header: 'Description',
      accessor: (row) => row.description || '-',
    },
    {
      header: 'Actions',
      accessor: (row) => {
        if (editingLtv?.id === row.id) {
          return (
            <div className="flex gap-2" data-test-id="edit-actions">
              <button
                onClick={handleSaveLtvEdit}
                className="text-green-600 hover:text-green-900"
                data-test-id="save-ltv-edit-button"
              >
                <Save className="h-4 w-4" data-test-id="save-ltv-edit-icon" />
              </button>
              <button
                onClick={() => setEditingLtv(null)}
                className="text-red-600 hover:text-red-900"
                data-test-id="cancel-ltv-edit-button"
              >
                <X className="h-4 w-4" data-test-id="cancel-ltv-edit-icon" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditLtv(row)}
              className="text-blue-600 hover:text-blue-900"
              data-test-id="edit-ltv-button"
            >
              <Edit className="h-4 w-4" data-test-id="edit-ltv-icon" />
            </button>
            <button
              onClick={() => handleDeleteLtv(row)}
              className="text-red-600 hover:text-red-900"
              data-test-id="delete-ltv-button"
            >
              <Trash2 className="h-4 w-4" data-test-id="delete-ltv-icon" />
            </button>
          </div>
        );
      },
    },
  ];

  const sasbColumns: Column<SASBPremiumDisplay>[] = [
    {
      header: 'Loan Size Threshold',
      accessor: (row) => formatCurrency(row.loan_size_threshold),
    },
    {
      header: 'Premium Rate',
      accessor: (row) => {
        if (editingSasb?.id === row.id) {
          return (
            <Input
              type="number"
              step="0.0001"
              value={editingSasb?.premium_rate || 0}
              onChange={(e) =>
                editingSasb &&
                setEditingSasb({
                  ...editingSasb,
                  premium_rate: parseFloat(e.target.value) || 0,
                })
              }
              className="w-24"
              data-test-id="edit-premium-rate-input"
            />
          );
        }
        return formatPercentage(row.premium_rate);
      },
    },
    {
      header: 'Description',
      accessor: (row) => row.description || '-',
    },
    {
      header: 'Actions',
      accessor: (row) => {
        if (editingSasb?.id === row.id) {
          return (
            <div className="flex gap-2" data-test-id="edit-actions">
              <button
                onClick={handleSaveSasbEdit}
                className="text-green-600 hover:text-green-900"
                data-test-id="save-sasb-edit-button"
              >
                <Save className="h-4 w-4" data-test-id="save-sasb-edit-icon" />
              </button>
              <button
                onClick={() => setEditingSasb(null)}
                className="text-red-600 hover:text-red-900"
                data-test-id="cancel-sasb-edit-button"
              >
                <X className="h-4 w-4" data-test-id="cancel-sasb-edit-icon" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditSasb(row)}
              className="text-blue-600 hover:text-blue-900"
              data-test-id="edit-sasb-button"
            >
              <Edit className="h-4 w-4" data-test-id="edit-sasb-icon" />
            </button>
            <button
              onClick={() => handleDeleteSasb(row)}
              className="text-red-600 hover:text-red-900"
              data-test-id="delete-sasb-button"
            >
              <Trash2 className="h-4 w-4" data-test-id="delete-sasb-icon" />
            </button>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div
        className="page-container"
        data-test-id="rpx-adjustments-page-container"
      >
        <div className="page-inner" data-test-id="rpx-adjustments-page-inner">
          <div
            className="flex items-center justify-center h-64"
            data-test-id="loading-wrapper"
          >
            <div
              className="text-lg text-gray-600"
              data-test-id="loading-rpx-adjustments"
            >
              Loading RPX adjustments...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container"
      data-test-id="rpx-adjustments-page-container"
    >
      <div className="page-inner" data-test-id="rpx-adjustments-page-inner">
        {/* Header */}
        <div className="mb-8" data-test-id="header-section">
          <div
            className="flex justify-between items-center"
            data-test-id="header-wrapper"
          >
            <div data-test-id="header-content">
              <h1 className="section-title mb-2" data-test-id="page-title">
                RPX Adjustments Management
              </h1>
              <p className="section-subtitle" data-test-id="page-subtitle">
                Manage LTV adjustment factors and SASB premiums for loan pricing
              </p>
            </div>
            <div className="flex gap-2" data-test-id="header-actions">
              <Button
                variant="outline"
                onClick={() =>
                  activeTab === 'ltv'
                    ? setShowLtvForm(!showLtvForm)
                    : setShowSasbForm(!showSasbForm)
                }
                className="flex items-center gap-2"
                data-test-id="add-adjustment-button"
              >
                <Plus className="h-4 w-4" data-test-id="add-adjustment-icon" />
                {activeTab === 'ltv'
                  ? 'Add LTV Adjustment'
                  : 'Add SASB Premium'}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6" data-test-id="tabs-section">
          <div className="flex border-b" data-test-id="tabs-wrapper">
            <button
              onClick={() => setActiveTab('ltv')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'ltv'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-test-id="ltv-adjustments-tab"
            >
              LTV Adjustments ({ltvAdjustments.length})
            </button>
            <button
              onClick={() => setActiveTab('sasb')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'sasb'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-test-id="sasb-premiums-tab"
            >
              SASB Premiums ({sasbPremiums.length})
            </button>
          </div>
        </div>

        {/* LTV Adjustments Tab */}
        {activeTab === 'ltv' && (
          <>
            {/* Add LTV Form */}
            {showLtvForm && (
              <Card className="p-6 mb-6" data-test-id="add-ltv-form-card">
                <h3
                  className="text-lg font-semibold mb-4"
                  data-test-id="add-ltv-form-title"
                >
                  Add LTV Adjustment
                </h3>
                <div
                  className="grid grid-cols-1 md:grid-cols-4 gap-4"
                  data-test-id="add-ltv-form-grid"
                >
                  <div data-test-id="ltv-min-field">
                    <Label htmlFor="ltv_min" data-test-id="ltv-min-label">
                      LTV Min (%)
                    </Label>
                    <Input
                      id="ltv_min"
                      type="number"
                      step="0.01"
                      value={ltvFormData.ltv_min * 100}
                      onChange={(e) =>
                        setLtvFormData({
                          ...ltvFormData,
                          ltv_min: parseFloat(e.target.value) / 100 || 0,
                        })
                      }
                      data-test-id="ltv-min-input"
                    />
                  </div>
                  <div data-test-id="ltv-max-field">
                    <Label htmlFor="ltv_max" data-test-id="ltv-max-label">
                      LTV Max (%)
                    </Label>
                    <Input
                      id="ltv_max"
                      type="number"
                      step="0.01"
                      value={ltvFormData.ltv_max * 100}
                      onChange={(e) =>
                        setLtvFormData({
                          ...ltvFormData,
                          ltv_max: parseFloat(e.target.value) / 100 || 0,
                        })
                      }
                      data-test-id="ltv-max-input"
                    />
                  </div>
                  <div data-test-id="adjustment-factor-field">
                    <Label
                      htmlFor="adjustment_factor"
                      data-test-id="adjustment-factor-label"
                    >
                      Adjustment Factor
                    </Label>
                    <Input
                      id="adjustment_factor"
                      type="number"
                      step="0.001"
                      value={ltvFormData.adjustment_factor}
                      onChange={(e) =>
                        setLtvFormData({
                          ...ltvFormData,
                          adjustment_factor: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-test-id="adjustment-factor-input"
                    />
                  </div>
                  <div data-test-id="ltv-description-field">
                    <Label
                      htmlFor="ltv_description"
                      data-test-id="ltv-description-label"
                    >
                      Description
                    </Label>
                    <Input
                      id="ltv_description"
                      type="text"
                      value={ltvFormData.description || ''}
                      onChange={(e) =>
                        setLtvFormData({
                          ...ltvFormData,
                          description: e.target.value,
                        })
                      }
                      data-test-id="ltv-description-input"
                    />
                  </div>
                </div>
                <div
                  className="flex justify-end gap-2 mt-4"
                  data-test-id="add-ltv-form-actions"
                >
                  <Button
                    variant="outline"
                    onClick={() => setShowLtvForm(false)}
                    data-test-id="cancel-ltv-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddLtvAdjustment}
                    data-test-id="add-ltv-submit-button"
                  >
                    Add Adjustment
                  </Button>
                </div>
              </Card>
            )}

            {/* LTV Adjustments Table */}
            <Card className="p-6" data-test-id="ltv-adjustments-table-card">
              <h3
                className="text-lg font-semibold mb-4"
                data-test-id="ltv-adjustments-table-title"
              >
                LTV Adjustment Factors ({ltvAdjustments.length} adjustments)
              </h3>
              <DataTable
                columns={ltvColumns}
                data={ltvAdjustments}
                keyExtractor={(row) =>
                  `ltv-${row.ltv_min}-${row.ltv_max}-${row.id}`
                }
                emptyMessage="No LTV adjustments found. Click 'Add LTV Adjustment' to create one."
                data-test-id="ltv-adjustments-table"
              />
            </Card>
          </>
        )}

        {/* SASB Premiums Tab */}
        {activeTab === 'sasb' && (
          <>
            {/* Add SASB Form */}
            {showSasbForm && (
              <Card className="p-6 mb-6" data-test-id="add-sasb-form-card">
                <h3
                  className="text-lg font-semibold mb-4"
                  data-test-id="add-sasb-form-title"
                >
                  Add SASB Premium
                </h3>
                <div
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  data-test-id="add-sasb-form-grid"
                >
                  <div data-test-id="loan-size-threshold-field">
                    <Label
                      htmlFor="loan_size_threshold"
                      data-test-id="loan-size-threshold-label"
                    >
                      Loan Size Threshold
                    </Label>
                    <Input
                      id="loan_size_threshold"
                      type="number"
                      value={sasbFormData.loan_size_threshold}
                      onChange={(e) =>
                        setSasbFormData({
                          ...sasbFormData,
                          loan_size_threshold: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-test-id="loan-size-threshold-input"
                    />
                  </div>
                  <div data-test-id="premium-rate-field">
                    <Label
                      htmlFor="premium_rate"
                      data-test-id="premium-rate-label"
                    >
                      Premium Rate (%)
                    </Label>
                    <Input
                      id="premium_rate"
                      type="number"
                      step="0.0001"
                      value={sasbFormData.premium_rate * 100}
                      onChange={(e) =>
                        setSasbFormData({
                          ...sasbFormData,
                          premium_rate: parseFloat(e.target.value) / 100 || 0,
                        })
                      }
                      data-test-id="premium-rate-input"
                    />
                  </div>
                  <div data-test-id="sasb-description-field">
                    <Label
                      htmlFor="sasb_description"
                      data-test-id="sasb-description-label"
                    >
                      Description
                    </Label>
                    <Input
                      id="sasb_description"
                      type="text"
                      value={sasbFormData.description || ''}
                      onChange={(e) =>
                        setSasbFormData({
                          ...sasbFormData,
                          description: e.target.value,
                        })
                      }
                      data-test-id="sasb-description-input"
                    />
                  </div>
                </div>
                <div
                  className="flex justify-end gap-2 mt-4"
                  data-test-id="add-sasb-form-actions"
                >
                  <Button
                    variant="outline"
                    onClick={() => setShowSasbForm(false)}
                    data-test-id="cancel-sasb-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddSasbPremium}
                    data-test-id="add-sasb-submit-button"
                  >
                    Add Premium
                  </Button>
                </div>
              </Card>
            )}

            {/* SASB Premiums Table */}
            <Card className="p-6" data-test-id="sasb-premiums-table-card">
              <h3
                className="text-lg font-semibold mb-4"
                data-test-id="sasb-premiums-table-title"
              >
                SASB Premiums ({sasbPremiums.length} premiums)
              </h3>
              <DataTable
                columns={sasbColumns}
                data={sasbPremiums}
                keyExtractor={(row) =>
                  `sasb-${row.loan_size_threshold}-${row.id}`
                }
                emptyMessage="No SASB premiums found. Click 'Add SASB Premium' to create one."
                data-test-id="sasb-premiums-table"
              />
            </Card>
          </>
        )}

        {/* Summary Statistics */}
        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8"
          data-test-id="summary-statistics"
        >
          <Card className="p-6" data-test-id="ltv-adjustments-count-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="ltv-adjustments-count-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="ltv-adjustments-count-title"
              >
                LTV Adjustments
              </h3>
              <BarChart3
                className="h-5 w-5 text-blue-600"
                data-test-id="ltv-adjustments-count-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="ltv-adjustments-count-value"
            >
              {ltvAdjustments.length}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="ltv-adjustments-count-label"
            >
              Active adjustments
            </p>
          </Card>
          <Card className="p-6" data-test-id="sasb-premiums-count-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="sasb-premiums-count-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="sasb-premiums-count-title"
              >
                SASB Premiums
              </h3>
              <DollarSign
                className="h-5 w-5 text-green-600"
                data-test-id="sasb-premiums-count-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="sasb-premiums-count-value"
            >
              {sasbPremiums.length}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="sasb-premiums-count-label"
            >
              Active premiums
            </p>
          </Card>
          <Card className="p-6" data-test-id="avg-ltv-adjustment-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="avg-ltv-adjustment-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="avg-ltv-adjustment-title"
              >
                Avg LTV Factor
              </h3>
              <TrendingUp
                className="h-5 w-5 text-purple-600"
                data-test-id="avg-ltv-adjustment-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="avg-ltv-adjustment-value"
            >
              {ltvAdjustments.length > 0
                ? (
                    ltvAdjustments.reduce(
                      (sum, adj) => sum + adj.adjustment_factor,
                      0
                    ) / ltvAdjustments.length
                  ).toFixed(3)
                : '0.000'}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="avg-ltv-adjustment-label"
            >
              Average factor
            </p>
          </Card>
          <Card className="p-6" data-test-id="avg-sasb-premium-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="avg-sasb-premium-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="avg-sasb-premium-title"
              >
                Avg SASB Rate
              </h3>
              <Settings
                className="h-5 w-5 text-orange-600"
                data-test-id="avg-sasb-premium-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="avg-sasb-premium-value"
            >
              {sasbPremiums.length > 0
                ? formatPercentage(
                    sasbPremiums.reduce(
                      (sum, prem) => sum + prem.premium_rate,
                      0
                    ) / sasbPremiums.length
                  )
                : '0.000%'}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="avg-sasb-premium-label"
            >
              Average premium
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RPXAdjustmentsPage;
